import {
  ICustomisationBaseAPI,
  IInlinePanelAPI,
  ICodePanelAPI,
  IUrlPanelAPI,
  IUnknownPanelAPI,
  TPanelAPI,
  IDataAPI,
  IEventAPI,
  ICustomisationEmptyAPI,
  ICustomisationAPI,
} from '@wix/editor-platform-host-integration-apis';
import { AppData } from '@wix/document-services-types';
import { ACTIONS, Action } from '@wix/component-protocol/schema';

import { getWorkerManager } from '../../../bootstrap/init';

import {
  CustomManifestAction,
  PlatformContext,
  isCustomManifestAction,
} from '../types';
import { getAppDataFromEditorPointer } from '../utils';
import {
  createEditorPlatformEditActionsError,
  EditorPlatformEditActionsErrorCode,
} from '../errors';

class CustomisationAPI implements ICustomisationBaseAPI {
  constructor(protected action: CustomManifestAction) {}

  hasCustomisation() {
    return !!this.action.execution;
  }
}

class CustomisationEmptyAPI implements ICustomisationEmptyAPI {
  public apiType: 'none' = 'none';

  hasCustomisation() {
    return false;
  }

  getCustomisation() {
    console.warn('There is no customisation for this action');
    return null;
  }
}

class InlinePanelAPI extends CustomisationAPI implements IInlinePanelAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.panel = ACTIONS.ACTION_TYPE.panel;
}

class UnknownPanelAPI extends InlinePanelAPI implements IUnknownPanelAPI {
  public panelContentType: typeof ACTIONS.PANEL_CONTENT_TYPE.UNKNOWN_PanelContentType =
    ACTIONS.PANEL_CONTENT_TYPE.UNKNOWN_PanelContentType;

  constructor(protected action: CustomManifestAction) {
    super(action);
  }

  getCustomisation() {
    console.warn('Panel content type is not supported');
    return null;
  }
}

class UrlPanelAPI extends InlinePanelAPI implements IUrlPanelAPI {
  public panelContentType: typeof ACTIONS.PANEL_CONTENT_TYPE.url =
    ACTIONS.PANEL_CONTENT_TYPE.url;
  readonly appData?: AppData;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    readonly context: PlatformContext,
  ) {
    super(action);
    this.appData = getAppDataFromEditorPointer(
      context.editorPointer,
      context.editorPointerAPI,
      context.documentServicesAPI,
    );
  }

  getCustomisation() {
    if (!this.appData) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionAppDataIsMissing,
      ).withActionName(this.actionName);
    }

    if (!this.action.execution.panel?.inline?.url) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionPanelUrlIsMissing,
      ).withActionName(this.actionName);
    }

    return {
      appDefinitionId: this.appData.appDefinitionId!,
      displayName: this.action.displayName!,
      getWorkerManager: getWorkerManager as any,
      url: this.action.execution.panel?.inline?.url,
      panelSize: this.action.execution.panel?.inline?.size,
    };
  }
}

class CodePanelAPI extends InlinePanelAPI implements ICodePanelAPI {
  public panelContentType: typeof ACTIONS.PANEL_CONTENT_TYPE.code =
    ACTIONS.PANEL_CONTENT_TYPE.code;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    readonly context: PlatformContext,
  ) {
    super(action);
  }

  getCustomisation() {
    const panelContent = this.context.manifestServicesAPI.internal.getPanel(
      this.context.editorPointer,
      this.actionName,
    );

    if (!panelContent) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionPanelContentMissing,
      ).withActionName(this.actionName);
    }

    return {
      panelContent,
      cssUrl: this.action?.execution?.panel?.inline?.code?.bundleUrl,
      panelSize: this.action.execution.panel?.inline?.size,
    };
  }
}

class PanelApiFactory {
  static createPanelAPI(
    action: CustomManifestAction,
    actionName: string,
    context: PlatformContext,
  ): TPanelAPI {
    if (
      action.execution.panel?.inline?.contentType ===
      ACTIONS.PANEL_CONTENT_TYPE.url
    ) {
      return new UrlPanelAPI(action, actionName, context);
    } else if (
      action.execution.panel?.inline?.contentType ===
      ACTIONS.PANEL_CONTENT_TYPE.code
    ) {
      return new CodePanelAPI(action, actionName, context);
    }

    return new UnknownPanelAPI(action);
  }
}

class DataAPI extends CustomisationAPI implements IDataAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.data = ACTIONS.ACTION_TYPE.data;

  constructor(protected action: CustomManifestAction) {
    super(action);
  }

  getCustomisation() {
    return {};
  }
}

class EventAPI extends CustomisationAPI implements IEventAPI {
  public apiType: typeof ACTIONS.ACTION_TYPE.event = ACTIONS.ACTION_TYPE.event;
  readonly appData?: AppData;

  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    readonly context: PlatformContext,
  ) {
    super(action);
    this.appData = getAppDataFromEditorPointer(
      context.editorPointer,
      context.editorPointerAPI,
      context.documentServicesAPI,
    );
  }

  getCustomisation() {
    if (!this.appData) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionAppDataIsMissing,
      ).withActionName(this.actionName);
    }
    return () =>
      this.context.documentServicesAPI.platform.notifyApplicationByAppDefId(
        this.appData!.appDefinitionId!,
        {
          eventType: this.action.execution.event?.event!,
          eventPayload: {
            editorPointer: this.context.editorPointer,
            id: this.actionName,
          },
        },
      );
  }
}

export function createCustomisationAPI(
  action: CustomManifestAction | Action,
  actionName: string,
  context: PlatformContext,
): ICustomisationAPI {
  if (!isCustomManifestAction(action)) {
    return new CustomisationEmptyAPI();
  }

  switch (action.execution.actionType) {
    case ACTIONS.ACTION_TYPE.panel:
      return PanelApiFactory.createPanelAPI(action, actionName, context);
    case ACTIONS.ACTION_TYPE.data:
      return new DataAPI(action);
    case ACTIONS.ACTION_TYPE.event:
      return new EventAPI(action, actionName, context);
    default:
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionExecutionTypeIsNotSupported,
      ).withActionName(actionName);
  }
}
